import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, useParams } from 'react-router-dom';
import { GuiaAyudaPanel } from '../ayuda/GuiaAyudaPanel';
import { 
  Building2, 
  Users, 
  Home, 
  BarChart3, 
  DollarSign, 
  TrendingUp,
  Target,
  LogOut,
  HelpCircle,
  Menu,
  FileText,
  FolderOpen,
  LayoutDashboard,
  Database,
  Receipt,
  Calculator,
  UserCircle,
  Bell,
  User,
  ChevronDown,
  Globe,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEdificioOp } from '../../contexts/EdificioOpContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { facturasPendientesApi, unidadesApi, edificiosApi, reclamosAdminApi } from '../../lib/api';

// Shows unit breadcrumb next to logo on mobile when inside /propietario/unidad/:id
const MobileUnitBreadcrumb = () => {
  const location = useLocation();
  const [unitInfo, setUnitInfo] = useState(null);
  
  const match = location.pathname.match(/\/propietario\/unidad\/([^/]+)/);
  const matchPortal = location.pathname.match(/\/propietario\/(?:renta|expensas)\/([^/]+)/);
  const unidadId = match ? match[1] : (matchPortal ? matchPortal[1] : null);

  useEffect(() => {
    if (!unidadId) { setUnitInfo(null); return; }
    unidadesApi.get(unidadId).then(res => {
      const esCowork = res.data.tipo === 'cowork';
      setUnitInfo({ edificio: res.data.edificio_nombre, unidad: esCowork ? 'Cowork' : res.data.nombre_unidad });
    }).catch(() => setUnitInfo(null));
  }, [unidadId]);

  if (!unitInfo) return null;
  return (
    <span className="text-xs text-slate-600 truncate">
      {unitInfo.edificio} · <span className="font-semibold text-slate-900">{unitInfo.unidad}</span>
    </span>
  );
};

const navItems = {
  propietario: [
    { to: '/propietario/novedades', icon: Bell, label: 'Novedades' },
    { to: '/propietario/renta', icon: TrendingUp, label: 'Mis Unidades - Renta' },
    { to: '/propietario/expensas', icon: Calculator, label: 'Expensas' },
    { to: '/propietario/mis-datos', icon: User, label: 'Mis Datos' },
    { to: '/propietario/reclamos', icon: MessageSquare, label: 'Reclamos y Sugerencias' },
  ],
  analista: [
    { to: '/analista/global-citizen', icon: Globe, label: 'Global Citizen' },
    { to: '/analista/propietarios', icon: Users, label: 'Propietarios' },
    { to: '/analista/edificios', icon: Building2, label: 'Edificios' },
    { to: '/analista/clientes', icon: UserCircle, label: 'Clientes' },
    { to: '/analista/documentos', icon: FolderOpen, label: 'Documentos' },
    { to: '/analista/cotizaciones', icon: DollarSign, label: 'Índices/Cotizaciones' },
    { to: '/analista/afip', icon: Receipt, label: 'Facturación AFIP' },
  ],
  comercial: [
    { to: '/comercial/leads', icon: Target, label: 'Leads' },
    { to: '/comercial/base-datos', icon: Database, label: 'Base de Datos' },
    { to: '/comercial/propietarios', icon: Users, label: 'Propietarios' },
    { to: '/comercial/edificios', icon: Building2, label: 'Edificios' },
    { to: '/comercial/documentos', icon: FolderOpen, label: 'Documentos' },
    { to: '/comercial/comisiones', icon: DollarSign, label: 'Comisiones' },
    { to: '/comercial/reporte', icon: TrendingUp, label: 'Reporte' },
  ],
  admin: [
    { to: '/admin/documentos', icon: FolderOpen, label: 'Documentos' },
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/reclamos', icon: MessageSquare, label: 'Reclamos y Sugerencias', badgeKey: 'reclamos_nuevos' },
    { to: '/admin/edificios', icon: Building2, label: 'Reporte Edificios' },
    { to: '/admin/unidades', icon: BarChart3, label: 'Reporte Unidades' },
    { to: '/admin/usuarios', icon: Users, label: 'Usuarios' },
    { to: '/admin/backups', icon: Database, label: 'Backups' },
  ],
  revisor: [
    { to: '/revisor/novedades', icon: Bell, label: 'Novedades' },
    { to: '/revisor/reclamos', icon: MessageSquare, label: 'Reclamos y Sugerencias', badgeKey: 'reclamos_nuevos' },
    { to: '/revisor/documentos', icon: FolderOpen, label: 'Documentos' },
    { to: '/revisor/edificios', icon: Building2, label: 'Edificios' },
  ],
  administrador_consorcio: [
    { to: '/expensas', icon: Calculator, label: 'Expensas' },
    { to: '/consorcio/documentos', icon: FolderOpen, label: 'Documentos' },
  ],
  inquilino: [
    { to: '/inquilino/novedades', icon: Bell, label: 'Novedades' },
    { to: '/inquilino/expensas', icon: Calculator, label: 'Expensas' },
  ],
  operaciones: [
    { to: '/operaciones/reservas', icon: LayoutDashboard, label: 'Reservas' },
    { to: '/operaciones/reclamos', icon: Bell, label: 'Reclamos' },
    { to: '/operaciones/configurar', icon: Building2, label: 'Configuración' },
  ],
  usuario_edificio: [
    { to: '/edificio/reservas', icon: LayoutDashboard, label: 'Reservas' },
    { to: '/edificio/incidencias', icon: Bell, label: 'Incidencias' },
    { to: '/edificio/mi-unidad', icon: Users, label: 'Mi unidad' },
  ],
};

const NavItem = ({ to, icon: Icon, label, onClick, badge, collapsed, indent }) => (
  <NavLink
    to={to}
    end={to === '/propietario' || to === '/analista'}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        'flex items-center gap-3 px-3 py-2.5 text-slate-600 rounded-lg transition-all duration-200',
        isActive
          ? 'bg-slate-100 text-[#1B4D3E] font-medium'
          : 'hover:bg-slate-100 hover:text-slate-900',
        collapsed && 'justify-center px-2',
        indent && !collapsed && 'pl-9'
      )
    }
    title={collapsed ? label : undefined}
    data-testid={`nav-${to.replace(/\//g, '-')}`}
  >
    <Icon className={cn('flex-shrink-0', indent ? 'w-4 h-4' : 'w-5 h-5')} strokeWidth={1.5} />
    {!collapsed && <span className="flex-1 whitespace-nowrap">{label}</span>}
    {!collapsed && badge > 0 && (
      <span className="px-2 py-0.5 text-xs font-semibold bg-amber-500 text-white rounded-full">
        {badge}
      </span>
    )}
  </NavLink>
);

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const [guiaOpen, setGuiaOpen] = useState(false);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [reclamosNuevos, setReclamosNuevos] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [edificiosList, setEdificiosList] = useState([]);

  // Roles que tienen acceso a navegar entre edificios desde el sidebar
  const ROLES_CON_EDIFICIOS = ['analista', 'comercial', 'revisor', 'administrador_consorcio'];
  const ROL_RUTA_EDIFICIOS = {
    analista: '/analista/edificio',
    comercial: '/comercial/edificio',
    revisor: '/revisor/edificio',
    administrador_consorcio: '/expensas',
  };

  // Fetch edificios for roles that navigate by building
  useEffect(() => {
    if (!ROLES_CON_EDIFICIOS.includes(user?.rol)) return;
    edificiosApi.list().then((r) => setEdificiosList(r.data || [])).catch(() => {});
  }, [user?.rol]);

  // Construye los items finales del sidebar.
  // Para roles que listan edificios, reemplaza el item "Edificios" por un grupo
  // expandido con un sub-item por edificio (sin pantalla intermedia de listado).
  const buildItemsForRole = (rol) => {
    const raw = navItems[rol] || [];
    if (!ROLES_CON_EDIFICIOS.includes(rol) || edificiosList.length === 0) return raw;
    const result = [];
    for (const it of raw) {
      if (it.label === 'Edificios' || it.label === 'Reporte Edificios' || it.label === 'Expensas') {
        // Header del grupo (no clickeable, sin "to")
        result.push({ isGroup: true, icon: it.icon || Building2, label: it.label });
        // Un sub-item por edificio
        for (const e of edificiosList) {
          result.push({
            to: `${ROL_RUTA_EDIFICIOS[rol]}/${e.edificio_id}`,
            icon: Building2,
            label: e.nombre,
            isSub: true,
          });
        }
      } else {
        result.push(it);
      }
    }
    return result;
  };

  const baseItems = buildItemsForRole(user?.rol);
  // (p. ej. Reservas / Mi unidad de usuario_edificio).
  const extraRoles = user?.rol === 'propietario'
    ? []
    : (user?.roles_extra || []).filter((r) => r !== user?.rol);
  const extraItems = extraRoles.flatMap((r) => buildItemsForRole(r));
  const items = [...baseItems, ...extraItems];

  // Fetch pending facturas count for analista (rol primario o roles_extra)
  const esAnalista = [user?.rol, ...(user?.roles_extra || [])].includes('analista');
  useEffect(() => {
    if (esAnalista) {
      const fetchPendingCount = async () => {
        try {
          const res = await facturasPendientesApi.list();
          setPendingCount(res.data.length);
        } catch (err) {
          console.error('Error fetching pending count:', err);
        }
      };
      fetchPendingCount();
      const interval = setInterval(fetchPendingCount, 30000);
      return () => clearInterval(interval);
    }
  }, [esAnalista]);

  // Contador de reclamos nuevos para revisor / admin
  useEffect(() => {
    const roles = [user?.rol, ...(user?.roles_extra || [])];
    if (!roles.includes('revisor') && !roles.includes('admin')) return;
    let active = true;
    const fetchNuevos = async () => {
      try {
        const res = await reclamosAdminApi.nuevosCount();
        if (active) setReclamosNuevos(res.data?.count || 0);
      } catch { /* noop */ }
    };
    fetchNuevos();
    const interval = setInterval(fetchNuevos, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [user?.rol, user?.roles_extra]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const collapsed = !isHovered;

  const SidebarContent = ({ onItemClick, forMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="border-b border-slate-200 p-2">
        {(forMobile || !collapsed) ? (
          <img 
            src="https://customer-assets.emergentagent.com/job_prop-manager-gc/artifacts/7ljwqsni_Logo%20GC.jpg"
            alt="Global Citizen"
            className="w-full h-auto"
          />
        ) : (
          <div className="w-10 h-10 mx-auto rounded-lg bg-[#1B4D3E] flex items-center justify-center">
            <span className="text-white font-bold text-sm">GC</span>
          </div>
        )}
      </div>

      {/* User info */}
      {(forMobile || !collapsed) && (
        <NavLink
          to="/mi-cuenta"
          onClick={onItemClick}
          className="block p-3 border-b border-slate-200 hover:bg-slate-100 transition-colors"
          data-testid="user-info-mi-cuenta"
          title="Ir a Mi cuenta"
        >
          <p className="text-sm font-medium text-slate-900 truncate">{user?.nombre}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded capitalize">
            {user?.rol}
          </span>
        </NavLink>
      )}

      {/* Building selector for operaciones */}
      {user?.rol === 'operaciones' && <OpEdificioSwitcher compact={!forMobile && collapsed} />}

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {items.map((item, idx) => {
          if (item.isGroup) {
            // Header del grupo (no clickeable)
            if (!forMobile && collapsed) {
              return (
                <div key={`group-${idx}`} className="flex justify-center py-2" title={item.label}>
                  <item.icon className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
                </div>
              );
            }
            return (
              <div
                key={`group-${idx}`}
                className="flex items-center gap-3 px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{item.label}</span>
              </div>
            );
          }
          return (
            <NavItem
              key={item.to}
              {...item}
              onClick={onItemClick}
              badge={item.badgeKey === 'reclamos_nuevos' ? reclamosNuevos : (item.hasBadge ? pendingCount : 0)}
              collapsed={!forMobile && collapsed}
              indent={item.isSub}
            />
          );
        })}
      </nav>

      {/* Ayuda (solo Administración de renta) + Logout */}
      <div className="p-2 border-t border-slate-200">
        {user?.rol === 'analista' && (
          <button
            onClick={() => setGuiaOpen(true)}
            className={cn(
              "flex items-center gap-3 w-full px-3 py-2.5 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors duration-200",
              !forMobile && collapsed && "justify-center px-2"
            )}
            title={collapsed && !forMobile ? "Guía de uso" : undefined}
            data-testid="guia-ayuda-btn"
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
            {(forMobile || !collapsed) && <span>Guía de uso</span>}
          </button>
        )}
        <button
          onClick={handleLogout}
          className={cn(
            "flex items-center gap-3 w-full px-3 py-2.5 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors duration-200",
            !forMobile && collapsed && "justify-center px-2"
          )}
          title={collapsed && !forMobile ? "Cerrar sesión" : undefined}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
          {(forMobile || !collapsed) && <span>Cerrar sesión</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {user?.rol === 'analista' && <GuiaAyudaPanel open={guiaOpen} onOpenChange={setGuiaOpen} />}
      {/* Desktop Sidebar - Collapsible on hover */}
      <aside 
        className={cn(
          "hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 bg-slate-50 border-r border-slate-200 transition-all duration-300 z-40 safe-pt",
          isHovered ? "lg:w-56" : "lg:w-14"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Menu Button & Sheet */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 mobile-header-safe flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <img 
            src="https://customer-assets.emergentagent.com/job_prop-manager-gc/artifacts/7ljwqsni_Logo%20GC.jpg"
            alt="Global Citizen"
            className="h-10 w-auto flex-shrink-0"
          />
          <MobileUnitBreadcrumb />
        </div>
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 safe-pt safe-pb">
            <SidebarContent onItemClick={() => setIsOpen(false)} forMobile={true} />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};

export default Sidebar;

const OpEdificioSwitcher = ({ compact = false }) => {
  const { edificios, edificioId, setEdificioId, edificioActivo } = useEdificioOp();
  if (!edificios.length) return null;

  if (compact) {
    return (
      <div className="px-2 py-2 border-b border-slate-200">
        <div className="relative w-full">
          <button
            type="button"
            className="w-full h-9 flex items-center justify-center bg-amber-50 hover:bg-amber-100 rounded-lg transition pointer-events-none"
            title={edificioActivo?.nombre}
          >
            <Building2 className="w-4 h-4 text-amber-700" />
          </button>
          <select
            value={edificioId}
            onChange={(e) => setEdificioId(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid="op-edif-switcher"
            aria-label="Edificio"
          >
            {edificios.map(e => (
              <option key={e.edificio_id} value={e.edificio_id}>{e.nombre}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-b border-slate-200">
      <span className="block text-[10px] uppercase font-semibold text-slate-400 tracking-wider mb-1">Edificio</span>
      <div className="relative">
        <div className="w-full flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg transition pointer-events-none">
          <span className="flex items-center gap-2 truncate">
            <Building2 className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <span className="font-semibold text-slate-900 text-sm truncate">{edificioActivo?.nombre || 'Elegir edificio'}</span>
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </div>
        <select
          value={edificioId}
          onChange={(e) => setEdificioId(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
          data-testid="op-edif-switcher"
          aria-label="Edificio"
        >
          {edificios.map(e => (
            <option key={e.edificio_id} value={e.edificio_id}>{e.nombre}</option>
          ))}
        </select>
      </div>
    </div>
  );
};
